import type { ReactNode } from 'react'
import { Button, Dropdown, Space, type ButtonProps, type MenuProps } from 'antd'
import { MoreHorizontal } from 'lucide-react'
import { hasAllPermissions, type PermissionCode } from '@super-mega/server/contracts/authorization'
import { useAccess } from '@/hooks/use-access.ts'
import { canPerformAction } from '@/utils/action-access.ts'

export type TableRowAction = Pick<ButtonProps, 'href' | 'target' | 'rel'> & {
  key: string
  label: ReactNode
  onClick?: () => void | Promise<void>
  render?: ReactNode
  danger?: boolean
  disabled?: boolean
  permissions?: readonly PermissionCode[]
}

/**
 * 列表行操作统一规则：最多直出两个按钮；超过两个时只直出第一个，其余收进“更多”。
 */
export function TableRowActions({
  actions,
  resource,
  maxVisibleActions = 2
}: {
  actions: TableRowAction[]
  resource?: string
  maxVisibleActions?: 1 | 2
}) {
  const { session } = useAccess()
  actions = actions.filter((action) => {
    if (action.permissions) return !!session && hasAllPermissions(session, action.permissions)
    return !resource || canPerformAction(session, resource, action.key)
  })
  const visibleCount = actions.length > maxVisibleActions ? 1 : actions.length
  const visibleActions = actions.slice(0, visibleCount)
  const overflowActions = actions.slice(visibleCount)

  const moreMenu: MenuProps = {
    items: overflowActions.map(({ danger, disabled, key, label, href, target, rel }) => ({
      danger,
      disabled,
      key,
      label:
        href && !disabled ? (
          <a href={href} target={target} rel={rel}>
            {label}
          </a>
        ) : (
          label
        )
    })),
    onClick: ({ key }) => {
      const action = overflowActions.find((candidate) => candidate.key === key)
      void action?.onClick?.()
    }
  }

  return (
    <Space className="whitespace-nowrap" size={8} wrap={false}>
      {visibleActions.map((action) => (
        <span key={action.key}>
          {action.render ?? (
            <Button
              href={action.href}
              target={action.target}
              rel={action.rel}
              danger={action.danger}
              disabled={action.disabled}
              onClick={() => void action.onClick?.()}
              size="small"
              type="link"
            >
              {action.label}
            </Button>
          )}
        </span>
      ))}
      {overflowActions.length > 0 ? (
        <Dropdown menu={moreMenu} placement="bottomRight" trigger={['click']}>
          <Button aria-label="更多操作" icon={<MoreHorizontal aria-hidden size={16} />} size="small" type="text">
            更多
          </Button>
        </Dropdown>
      ) : null}
    </Space>
  )
}
